/**
 * Voyage Radar vessel popup markup.
 *
 * Pure string builders — no Leaflet, no Angular, no component state — so any map
 * component that draws Voyage Radar vessels can render the same card, and the
 * escaping can be unit-tested on its own.
 *
 * The classes (`ais-vessel-popup`, `ais-popup__*`) are styled globally in
 * `styles.scss`, not in a component stylesheet: Leaflet mounts popups outside
 * the component's DOM, so scoped styles would never reach them.
 */

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const ESCAPES: { [char: string]: string } = {
  '<': '&lt;',
  '>': '&gt;',
  '&': '&amp;',
  '"': '&quot;',
};

/** Everything interpolated into the popup goes through this — the properties
 *  come from an upstream feed, so treat them as untrusted.
 *
 *  Exported for other STRING BUILDERS only. An Angular template escapes its own
 *  interpolations, so a component that renders these fields through a binding
 *  must not call this first — that prints `&amp;` at the user. */
export function esc(value: any): string {
  return String(value ?? '').replace(/[<>&"]/g, (c) => ESCAPES[c]);
}

/** "Mon, 27 Jul 2026 17:24:08 UTC", or "—" when the stamp is missing/unparseable. */
function formatPopupTime(raw: any): string {
  if (!raw) return '—';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${DAYS[d.getUTCDay()]}, ${pad(d.getUTCDate())} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())} UTC`
  );
}

/** One key/value line, or nothing at all when there is no value to show. */
function row(label: string, val: any, suffix = ''): string {
  if (val == null || val === '' || val === 'N/A') return '';
  return (
    `<div class="ais-popup__row"><span class="ais-popup__k">${label}:</span>` +
    `<span class="ais-popup__v">${esc(val)}${suffix}</span></div>`
  );
}

/**
 * Decimal places on a printed vessel position.
 *
 * The WeatherEx point tooltip prints 2, and this row copies its grammar
 * ("Lat: …, Lng: …") so the two read as one product — but not its precision. A
 * WeatherEx value describes a forecast CELL kilometres across, where the third
 * decimal would be invented; an AIS fix is a single hull, and 2 dp rounds it
 * into a ~1.1 km box, which is a different berth, sometimes a different
 * anchorage. 4 dp (~11 m) is the position the operator can actually act on.
 */
const POSITION_DP = 4;

/**
 * A coordinate only if it is a real, in-range number — never a printed NaN.
 *
 * The empty-string guard is load-bearing, not defensive noise:
 * `toVoyageVesselFeature` writes `Lat: ''` for a hull with no position, and
 * `Number('')` is 0 — so without it a vessel whose fix is missing would be
 * printed as sitting at 0.0000, 0.0000 in the Gulf of Guinea.
 */
function coord(value: any, limit: number): number | null {
  if (value == null || (typeof value === 'string' && !value.trim())) return null;
  const n = Number(value);
  return Number.isFinite(n) && Math.abs(n) <= limit ? n : null;
}

/**
 * "Lat: 1.2646, Lng: 103.8201" — the same label grammar the WeatherEx point
 * tooltip uses, so a coordinate reads the same wherever the map shows one.
 *
 * Renders nothing at all when either half is missing or out of range: a hull
 * whose fix did not survive the feed is better shown without a position than
 * with a plausible-looking one at the equator.
 */
function positionRow(p: any): string {
  const lat = coord(p['Lat'] ?? p['lat'] ?? p['latitude'], 90);
  const lng = coord(p['Lng'] ?? p['lng'] ?? p['longitude'], 180);
  if (lat == null || lng == null) return '';
  return (
    `<div class="ais-popup__row"><span class="ais-popup__k">Position:</span>` +
    `<span class="ais-popup__v ais-popup__pos">` +
    `Lat: ${lat.toFixed(POSITION_DP)}, Lng: ${lng.toFixed(POSITION_DP)}</span></div>`
  );
}

/**
 * The popup's inner rows, without the card wrapper.
 *
 * Optional fields (Heading, IMO, Call Sign, Flag, Country, Length, Beam, Rate of
 * Turn) render only when present, so a history track-point — which carries far
 * fewer fields than a live bbox feature — still produces a compact card.
 *
 * Both the mapped property names ('Ship Type') and the raw upstream ones
 * (`shipType`) are accepted, because this is called with either shape.
 */
export function buildVoyagePopupBody(properties: any, actions = ''): string {
  const p = properties || {};
  // `||`, not `??`: a vessel that broadcasts no name arrives as '' and
  // `toVoyageVesselFeature` copies it through (it only skips null/undefined), so
  // `??` handed the card an empty heading instead of firing these fallbacks.
  // MMSI 0 is not a valid identity either, so it falls through the same way.
  const name = esc(p['Name'] || p['name'] || 'Unknown');
  const mmsi = esc(p['MMSI'] || p['mmsi'] || '—');
  const sog = p['Speed'] != null ? `${esc(p['Speed'])} kn` : '—';
  const cog = p['Course'] != null ? `${esc(Math.round(Number(p['Course'])))}°` : '—';
  // Only a literal hex colour is allowed through — it is interpolated into a
  // style attribute, where escaping alone would not make an arbitrary value safe.
  const color = /^#[0-9a-fA-F]{3,8}$/.test(String(p['Color'] ?? ''))
    ? String(p['Color'])
    : '#6B7280';
  const time = formatPopupTime(p['Timestamp UTC'] ?? p['time_utc']);
  const hdgRaw = p['Heading'] ?? p['heading'];
  const hdg = hdgRaw != null ? Math.round(Number(hdgRaw)) : null;

  return (
    `<div class="ais-popup__head"><span class="ais-popup__dot" style="background:${color}"></span>` +
      `<span class="ais-popup__name">${name}</span>${actions}</div>` +
    `<div class="ais-popup__row"><span class="ais-popup__k">MMSI:</span><span class="ais-popup__mmsi">${mmsi}</span></div>` +
    // Who, then WHERE, then the rest — a position belongs with the identity it
    // describes, not buried under the dimensions.
    positionRow(p) +
    row('Type', p['Ship Type'] ?? p['shipType']) +
    row('Status', p['Nav Status'] ?? p['navStatus']) +
    `<div class="ais-popup__row"><span class="ais-popup__k">SOG:</span><span class="ais-popup__v">${sog}</span></div>` +
    `<div class="ais-popup__row"><span class="ais-popup__k">COG:</span><span class="ais-popup__v">${cog}</span></div>` +
    (hdg != null
      ? `<div class="ais-popup__row"><span class="ais-popup__k">Heading:</span><span class="ais-popup__v">${esc(hdg)}°</span></div>`
      : '') +
    row('IMO', p['IMO'] ?? p['imo']) +
    row('Call Sign', p['Call Sign'] ?? p['callSign']) +
    row('Flag', p['Flag'] ?? p['flag']) +
    row('Country', p['Country'] ?? p['country']) +
    row('Length', p['Length'] ?? p['length'], ' m') +
    row('Beam', p['Beam'] ?? p['beam'], ' m') +
    // The lookup-only fields (GET /v1/vessel): present after a "Get latest",
    // absent on bbox/search data — `row()` renders nothing for those.
    row('Destination', p['Destination'] ?? p['destination'] ?? p['matchedDestination']) +
    row('From Port', p['From Port'] ?? p['fromPort']) +
    row('Max Draught', p['Max Draught'] ?? p['maxDraught'], ' m') +
    row('Rate of Turn', p['Rate of Turn'] ?? p['rate_of_turn']) +
    `<div class="ais-popup__row"><span class="ais-popup__k">Time:</span><span class="ais-popup__time">${time}</span></div>`
  );
}

/** The action set a vessel popup may carry — resolved by the caller per hull. */
export interface VoyagePopupActions {
  /** Cost of the by-MMSI lookup; null/undefined hides the update button. */
  lookupCredits?: number | null;
  /**
   * Cost of a port-call history; null/undefined hides the anchor button.
   *
   * Offered on EVERY hull, not only saved ones: port calls are a by-MMSI
   * question and any arrow on the chart is a legitimate subject — consistent
   * with the paid update beside it.
   */
  portCallCredits?: number | null;
  /**
   * Cost of a nearby-vessel search; null/undefined hides the radar button.
   *
   * The other half of the question the anchor beside it answers: port calls say
   * where this hull has BEEN, nearby says what is WITH it right now. Offered on
   * the same terms — any arrow on the chart is a legitimate centre — and priced
   * from the same catalogue the panel quotes, so the two paid per-vessel
   * actions on this card always name the numbers the sensor actually charges.
   *
   * The search is centred on a POSITION, which this card does not carry: the
   * server resolves it from the hull's stored fix, and falls back to the
   * coordinates the map controller sends from the feature this marker was drawn
   * from. A hull with neither is refused, free, with a message telling the user
   * to press Update first — which is the button immediately to the left.
   */
  nearbyCredits?: number | null;
  /**
   * 'add' offers the free save — free because an AOI refresh has already paid
   * for this hull's data. Null on a vessel already saved: the Saved Vessels
   * section owns removal, and a card that could delete a record was a
   * permanent act sitting one stray click from a read-only one.
   */
  save?: 'add' | null;
  /**
   * "Take off the map" for a LOCATED (amber) marker — the marker only, and the
   * vessel stays saved. Never on snapshot hulls: the snapshot
   * layer's decluttering tools are the type filter and clusters, not per-hull
   * removal, so there would be nothing for the button to act on.
   */
  removeFromMap?: boolean;
}

/**
 * The complete Voyage Radar vessel popup — an info card with a few icon actions
 * in its header.
 *
 * They live in the header rather than in a menu under the card. Full-width
 * buttons made a card that is already a dozen rows tall grow by another third,
 * and the card is a thing to READ — the actions are an aside. Track actions
 * live in the toolbar's trace panel for the same reason.
 *
 * At most FIVE of the six ever render together, because the first two are
 * mutually exclusive: the free save (only while the hull is NOT saved) or
 * take-off-the-map (only while it IS drawn as a located marker), then the paid
 * by-MMSI update, then port calls, then nearby vessels, then close. Each icon's
 * whole label is its tooltip — which is where a price has to live.
 *
 * Deleting the SAVED RECORD is deliberately not here. It is the one
 * permanent act in this set, and it belongs where its consequence is legible:
 * the Saved Vessels section, next to the row it would erase.
 *
 * Buttons are plain markup — Leaflet popups live outside Angular — so the map
 * controller click-delegates on `[data-mmsi]` + the class. The icons come from
 * the globally imported Material Icons font, the same one `mat-icon` uses.
 */
export function buildVoyagePopup(
  properties: any,
  opts?: VoyagePopupActions,
): string {
  const p = properties || {};
  const mmsi = String(p['MMSI'] || p['mmsi'] || '').trim();
  const actions: string[] = [];
  if (mmsi && opts?.save === 'add') {
    actions.push(
      `<button type="button" class="ais-popup__act ais-popup__save" data-mmsi="${esc(mmsi)}" ` +
      `title="Watch vessel" aria-label="Watch vessel">` +
      `<span class="material-icons-outlined">bookmark_add</span></button>`,
    );
  }
  if (mmsi && opts?.lookupCredits != null) {
    const c = opts.lookupCredits;
    // One button, two jobs: the card starts without the lookup-only fields
    // (Destination, From Port, Max Draught, Rate of Turn), so the first press
    // fills them in and every later one re-checks them along with the fix.
    // "Update" is the one verb true of both.
    const label = `Update · ${esc(c)} ${c === 1 ? 'credit' : 'credits'}`;
    actions.push(
      `<button type="button" class="ais-popup__act ais-popup__latest" data-mmsi="${esc(mmsi)}" ` +
      `title="${label} — latest position and full details" aria-label="${label}">` +
      `<span class="material-icons-outlined">refresh</span></button>`,
    );
  }
  if (mmsi && opts?.portCallCredits != null) {
    const c = opts.portCallCredits;
    // One click opens the drawer AND fetches, every time — so the price is on
    // the button's own face rather than discovered after a panel slides open.
    const label = `Port calls · ${esc(c)} ${c === 1 ? 'credit' : 'credits'}`;
    actions.push(
      `<button type="button" class="ais-popup__act ais-popup__ports" data-mmsi="${esc(mmsi)}" ` +
      `title="${label} — arrivals, departures and time in port" aria-label="${label}">` +
      `<span class="material-icons-outlined">anchor</span></button>`,
    );
  }
  if (mmsi && opts?.nearbyCredits != null) {
    const c = opts.nearbyCredits;
    // Next to port calls because the two are the same kind of act — one click
    // that opens a drawer AND buys the answer in it — so the price is on the
    // button's own face here too, rather than discovered after a panel slides
    // open. What it does NOT choose is the radius: that is the drawer's own
    // "within __ km", remembered from the last search, so this button is
    // "search around this hull" and never "search 10 km around this hull".
    //
    // A PAID action, and the only thing that says so before the click is the
    // amber the paid family carries — which comes from the
    // `.ais-popup__latest, .ais-popup__ports` rule in `styles.scss`, not from
    // `.ais-popup__act`. This class has to join that selector or the button
    // renders bare beside two that do not.
    const label = `Nearby vessels · ${esc(c)} ${c === 1 ? 'credit' : 'credits'}`;
    actions.push(
      `<button type="button" class="ais-popup__act ais-popup__nearby" data-mmsi="${esc(mmsi)}" ` +
      `title="${label} — who is around this hull right now" aria-label="${label}">` +
      `<span class="material-icons-outlined">radar</span></button>`,
    );
  }
  if (mmsi && opts?.removeFromMap) {
    // `layers_clear` because that is what this gesture means everywhere else in
    // Voyage Radar — both ON MAP bars and the Saved Vessels row use it for
    // exactly this, and it is the reversible one: the record stays, one click
    // redraws it.
    const label = 'Take off the map — stays in Watched Vessels';
    actions.push(
      `<button type="button" class="ais-popup__act ais-popup__offmap" data-mmsi="${esc(mmsi)}" ` +
      `title="${label}" aria-label="${label}">` +
      `<span class="material-icons-outlined">layers_clear</span></button>`,
    );
  }
  // Always last, and always present: the card is opened by a click on a hull,
  // and a click elsewhere on the map is the only other way to dismiss it —
  // which on a crowded chart means clicking another vessel by accident.
  actions.push(
    `<button type="button" class="ais-popup__act ais-popup__close" ` +
    `title="Close" aria-label="Close">` +
    `<span class="material-icons-outlined">close</span></button>`,
  );
  const menu = actions.length
    ? `<span class="ais-popup__acts">${actions.join('')}</span>`
    : '';
  // The hull is stamped on the card ROOT, not just on its buttons: four
  // different layers open this card (live, history, playback, located) and only
  // one of them goes through the map controller, so a `popupopen` listener
  // reading this attribute is the only way it can know whose card is on screen.
  const stamp = mmsi ? ` data-mmsi="${esc(mmsi)}"` : '';
  return `<div class="ais-popup"${stamp}>${buildVoyagePopupBody(properties, menu)}</div>`;
}

/** How many hulls a cluster popup names before it summarises the rest. */
const CLUSTER_POPUP_LIMIT = 14;

/**
 * The card for a cluster that zoom cannot break apart — vessels moored on top of
 * one another, or a map already at its deepest zoom.
 *
 * Clicking a cluster normally frames its members, so this only appears where
 * that would do nothing. A count with no way to see what is inside it is a dead
 * end: the bubble says fourteen, and the operator's next question is which
 * fourteen. Names only, because a berth full of hulls is a list to read, not a
 * set of cards to page through — one click deeper on any of them is the vessel
 * popup itself.
 */
export function buildVoyageClusterPopup(
  vessels: { name?: any; mmsi?: any }[]
): string {
  const total = vessels.length;
  const shown = vessels.slice(0, CLUSTER_POPUP_LIMIT);
  const rest = total - shown.length;
  const rows = shown
    .map((v) => {
      // Same fallback chain as the vessel card: a hull that broadcasts no name
      // is still identified by its MMSI, and one with neither is still a hull.
      const name = esc(v.name || v.mmsi || 'Unknown');
      const mmsi = v.name && v.mmsi ? `<span class="ais-popup__k">${esc(v.mmsi)}</span>` : '';
      return `<div class="ais-popup__row"><span class="ais-popup__v">${name}</span>${mmsi}</div>`;
    })
    .join('');
  const more = rest > 0
    ? `<div class="ais-popup__row"><span class="ais-popup__k">+ ${rest} more</span></div>`
    : '';
  return (
    `<div class="ais-popup ais-popup--cluster">` +
      `<div class="ais-popup__head"><span class="ais-popup__name">${total} vessels here</span></div>` +
      rows +
      more +
    `</div>`
  );
}

/**
 * How many nearby rows the POI popup renders at once. Higher than the
 * cluster's cap: this list IS the product of a POI refresh (there is no map
 * layer behind it), so it earns more height before it truncates.
 *
 * A cap alone would strand the tail — a 1000 km radius over a busy coast
 * returns hundreds — so the filter box searches the WHOLE list and renders its
 * matches through the same cap. Nothing is unreachable; only the default view
 * is bounded.
 */
const POI_POPUP_LIMIT = 60;

/** One /v1/vessels/nearby row — the ONLY fields that endpoint returns. */
export interface VoyagePoiNearbyVessel {
  mmsi: any;
  name?: any;
  shipType?: any;
  sog?: any;
  cog?: any;
  distanceKm?: any;
}

/** Per-render context the POI list needs from the controller. */
export interface VoyagePoiPopupContext {
  /** Cost of the per-vessel lookup a row click triggers. */
  lookupCredits: number;
  /** True when the hull is already drawn — its row flies for free instead. */
  onMap: (mmsi: string) => boolean;
  /** The radius the server actually served, for the subtitle. */
  radiusKm?: number | null;
  /**
   * The hull whose lookup is in flight, if any.
   *
   * Held in the CONTEXT rather than stamped onto the DOM by the click handler,
   * because the filter re-renders these rows from scratch on every keystroke —
   * a mark written straight to the clicked element would vanish the moment the
   * operator typed while waiting.
   */
  pendingMmsi?: string | null;
}

// ── Nearby-row field formatters ─────────────────────────────────────────────
//
// Exported, although only this file builds the POI popup that uses them today.
// The per-vessel nearby drawer renders the SAME six fields off the SAME
// endpoint, and every rule below is a statement about the FEED rather than
// about this popup: 102.3 kn and 360° are the AIS sentinels for "not
// reported", 'n/a'/'unknown'/'null' are the feed's ways of saying nothing, and
// ", all ships of this type" is an ITU suffix that is pure repetition in a
// list. A second surface re-deriving them is how one product ends up with two
// answers to "is 0 knots a speed" — and 0 knots is moored, which is a real
// answer, so the two would differ on a hull sitting at a berth.

/**
 * "180 m" · "3.4 km" · "27 km" — a nearby distance is a glance, not a fix.
 *
 * Metres below a kilometre because that band is where the list is densest: a
 * harbour returns a run of hulls that all print "0.2 km" at one decimal, and a
 * column of identical numbers cannot be the thing the list is sorted by.
 */
export function fmtDistanceKm(raw: any): string | null {
  const n = Number(raw);
  if (raw == null || raw === '' || !Number.isFinite(n)) return null;
  if (n < 1) return `${Math.round(n * 1000)} m`;
  return n < 10 ? `${n.toFixed(1)} km` : `${Math.round(n)} km`;
}

/**
 * Strings the AIS feed uses for "the vessel did not say", which must render as
 * nothing rather than as a value. `row()` above already drops 'N/A'; this is
 * the same rule for the list's own fields.
 */
const POI_ABSENT = new Set(['', 'n/a', 'not available', 'unknown', 'undefined', 'null']);

export function poiText(raw: any): string | null {
  const s = String(raw ?? '').trim();
  return s && !POI_ABSENT.has(s.toLowerCase()) ? s : null;
}

/**
 * "Cargo, all ships of this type" → "Cargo".
 *
 * The operator sends the ITU category verbatim, and that suffix is on most of
 * them — so in a list it is pure repetition that pushes the part that differs
 * off the end of a 300px card. Only the boilerplate is removed; any type the
 * feed spells differently is left exactly as it came.
 */
export function poiShipType(raw: any): string | null {
  const s = poiText(raw);
  if (!s) return null;
  return s.replace(/,\s*all ships of this type$/i, '').trim() || null;
}

/**
 * Course over ground, or null when the vessel is not reporting one.
 *
 * 360° is the AIS sentinel for "course not available" — printing it as a
 * heading claims the vessel is pointing due north, which is a different
 * statement from silence. Values are normalised into [0, 360).
 */
export function poiCog(raw: any): number | null {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n >= 360 || n < 0) return null;
  return Math.round(n) % 360;
}

/**
 * Speed over ground in knots, or null when unreported. 102.3 is the AIS
 * sentinel for "speed not available"; 0 is a real, meaningful value (moored),
 * so it must survive.
 */
export function poiSog(raw: any): number | null {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0 || n >= 102.3) return null;
  return n;
}

/** One rendered row of the nearby list. */
function poiRow(v: VoyagePoiNearbyVessel, ctx: VoyagePoiPopupContext): string {
  const mmsi = String(v?.mmsi ?? '').trim();
  const name = poiText(v?.name);
  // A silent hull is still a hull: its MMSI becomes the heading, and the
  // sub-line then omits the duplicate.
  const heading = esc(name || mmsi || 'Unknown');
  const dist = fmtDistanceKm(v?.distanceKm);
  const onMap = !!mmsi && ctx.onMap(mmsi);

  const sog = poiSog(v?.sog);
  const cog = poiCog(v?.cog);
  const sub = [
    // MMSI leads the sub-line: a row click BUYS this vessel's details, and the
    // MMSI is the identity being bought — a name alone is not unique.
    name && mmsi ? esc(mmsi) : null,
    poiShipType(v?.shipType) ? esc(poiShipType(v?.shipType)) : null,
    sog != null ? `${esc(sog)} kn` : null,
    cog != null ? `COG ${esc(cog)}°` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  // A click on this row starts an opaque network round trip that ends with the
  // map flying somewhere else entirely. Without a mark ON THE ROW, the only
  // evidence the click registered is the result — so the operator's reading of
  // a slow request is that nothing happened, and the second click is refused
  // in silence by the one-lookup-at-a-time guard.
  const busy = !!mmsi && String(ctx.pendingMmsi ?? '').trim() === mmsi;

  const badge = busy
    ? `<span class="ais-popup__poispin" aria-hidden="true"></span>`
    : onMap
      ? `<span class="ais-popup__poionmap">On map</span>`
      : '';
  const title = busy
    ? 'Fetching this vessel’s latest details…'
    : onMap
      ? 'Already on the map — go to it'
      : `Fetch latest details and place on the map · ${ctx.lookupCredits} ` +
        `${ctx.lookupCredits === 1 ? 'credit' : 'credits'}`;
  // The row's own data stays on screen — it is what the operator just chose by
  // — so the sub-line is the one line that changes, to say the click landed.
  const subLine = busy ? 'Fetching latest position and details…' : sub;

  return (
    `<button type="button" class="ais-popup__poirow${onMap ? ' is-onmap' : ''}` +
      `${busy ? ' is-busy' : ''}" data-mmsi="${esc(mmsi)}" title="${title}"` +
      `${busy ? ' aria-busy="true"' : ''}>` +
      `<span class="ais-popup__poimain">` +
        `<span class="ais-popup__poiname">${heading}</span>${badge}` +
        (dist ? `<span class="ais-popup__poidist">${esc(dist)}</span>` : '') +
      `</span>` +
      (subLine ? `<span class="ais-popup__poisub">${subLine}</span>` : '') +
    `</button>`
  );
}

/**
 * The scrolling body of the POI list — count line, rows, overflow note.
 *
 * Exported because the filter box re-renders exactly this, in place: the
 * controller swaps the container's innerHTML on every keystroke rather than
 * rebuilding the popup, so the input keeps focus and its caret.
 *
 * `query` matches name OR MMSI against the FULL list, which is what makes the
 * hulls past the cap reachable at all.
 */
export function buildVoyagePoiNearbyRows(
  vessels: VoyagePoiNearbyVessel[],
  ctx: VoyagePoiPopupContext,
  query = '',
): string {
  const all = Array.isArray(vessels) ? vessels : [];
  const q = String(query ?? '').trim().toLowerCase();
  const matches = q
    ? all.filter((v) => {
        const name = String(v?.name ?? '').toLowerCase();
        const mmsi = String(v?.mmsi ?? '').toLowerCase();
        return name.includes(q) || mmsi.includes(q);
      })
    : all;

  if (!matches.length) {
    const msg = q
      ? `No vessel here matches “${esc(query.trim())}”`
      : 'No vessels reported in range';
    return `<div class="ais-popup__poiempty">${msg}</div>`;
  }

  const shown = matches.slice(0, POI_POPUP_LIMIT);
  const rest = matches.length - shown.length;
  // Says what the list IS showing, not just what exists — the old copy put
  // "+140 more" at the foot of a scroller, where it was never read.
  const meta = q
    ? `${matches.length} ${matches.length === 1 ? 'match' : 'matches'}` +
      (rest > 0 ? ` · nearest ${shown.length} shown` : '')
    : rest > 0
      ? `Nearest ${shown.length} of ${all.length} — filter to reach the rest`
      : `${all.length} ${all.length === 1 ? 'vessel' : 'vessels'}, nearest first`;

  return (
    `<div class="ais-popup__poimeta">${meta}</div>` +
    shown.map((v) => poiRow(v, ctx)).join('')
  );
}

/**
 * The POI target's popup — the list of vessels near the tasked point.
 *
 * A POI refresh (/v1/vessels/nearby) returns NO positions, so this list is the
 * whole result: nothing is drawn until a row is clicked, which buys that one
 * vessel's full details (position included) via the by-MMSI lookup and places
 * it like a search result. Rows already on the map are badged and fly for free.
 *
 * Rows are real <button>s — keyboard-reachable, and the same click-delegate
 * pattern as the card's header actions (`data-mmsi` + a class, wired in
 * `ensurePopupActionsWired`; Leaflet popups render outside Angular).
 */
export function buildVoyagePoiNearbyPopup(
  vessels: VoyagePoiNearbyVessel[],
  ctx: VoyagePoiPopupContext,
): string {
  const list = Array.isArray(vessels) ? vessels : [];
  const total = list.length;
  const radius = Number(ctx.radiusKm);
  const within =
    Number.isFinite(radius) && radius > 0
      ? ` within ${esc(Math.round(radius))} km`
      : '';

  // Offered once the list is long enough to be worth searching — below that it
  // is a control that costs height and saves nothing.
  const filter =
    total > 12
      ? `<input type="search" class="ais-popup__poifilter" ` +
        `placeholder="Filter by name or MMSI" aria-label="Filter nearby vessels">`
      : '';

  // Priced on the FULL list, not the rendered page: the answer to "does
  // clicking cost me anything" must not change as the user scrolls or filters.
  const anythingToBuy = list.some((v) => {
    const m = String(v?.mmsi ?? '').trim();
    return m && !ctx.onMap(m);
  });
  // A footer, and deliberately not a trailing row: at the bottom of a
  // 60-row scroller the price was below the fold on every screen that
  // mattered, which meant the first click was always the one made without it.
  const foot = anythingToBuy
    ? `<div class="ais-popup__poifoot">Click a vessel for its latest details and position — ` +
      `${esc(ctx.lookupCredits)} ${ctx.lookupCredits === 1 ? 'credit' : 'credits'} each</div>`
    : '';

  // On the ROOT as well as on the row: one lookup runs at a time, so while one
  // is in flight every OTHER row is an offer that would be refused. The class
  // is what stands them down (see `styles.scss`) — and it is baked into the
  // markup, not only toggled by the controller, so a card reopened mid-request
  // comes back in the same state it was dismissed in.
  const busy = !!String(ctx.pendingMmsi ?? '').trim();

  return (
    `<div class="ais-popup ais-popup--poi${busy ? ' is-busy' : ''}">` +
      `<div class="ais-popup__poihead">` +
        `<div class="ais-popup__head">` +
          `<span class="ais-popup__name">${total} ${total === 1 ? 'vessel' : 'vessels'} nearby${within}</span>` +
          `<span class="ais-popup__acts">` +
            `<button type="button" class="ais-popup__act ais-popup__close" ` +
            `title="Close" aria-label="Close">` +
            `<span class="material-icons-outlined">close</span></button>` +
          `</span>` +
        `</div>` +
        filter +
      `</div>` +
      `<div class="ais-popup__poirows">${buildVoyagePoiNearbyRows(list, ctx)}</div>` +
      foot +
    `</div>`
  );
}
